import { type UseAuthReturn } from '@leagueplatform/auth-utils';
import { captureAppError } from '@leagueplatform/observability';
import { useNavigate, useLocation } from '@leagueplatform/routing';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { STORED_DEEPLINK_SESSION_STORAGE_KEY } from 'common/constants';
import { useEffect, useState } from 'react';

/**
 * This hook handles the complete authentication redirect flow:
 * 1. When not authenticated: stores the current path as a deep-link and redirects to login
 * 2. When authenticated: navigates to stored deep-link (if any) or home page
 *
 * Uses internal state to prevent race conditions with other components that might
 * also try to handle post-authentication navigation.
 */

export const useAuthLoginRedirect = (
  isAuthenticated: UseAuthReturn['isAuthenticated'],
): { hasHandledPostAuth: boolean } => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasHandledPostAuth, setHasHandledPostAuth] = useState(false);

  // Reset state when authentication status changes to false (new auth cycle)
  useEffect(() => {
    if (isAuthenticated === false) {
      setHasHandledPostAuth(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated === false) {
      const { pathname, search } = location;
      const storedDeeplinkPath = pathname + search;

      sessionStorage.setItem(
        STORED_DEEPLINK_SESSION_STORAGE_KEY,
        storedDeeplinkPath,
      );

      navigate(APP_MANAGED_ROUTES.SIGN_IN);
    }

    if (isAuthenticated === true && !hasHandledPostAuth) {
      const storedDeeplink = sessionStorage.getItem(
        STORED_DEEPLINK_SESSION_STORAGE_KEY,
      );

      if (storedDeeplink) {
        try {
          // Validate the deep-link before navigating to prevent open redirect vulnerabilities
          const url = new URL(storedDeeplink, window.location.origin);
          navigate(url.pathname + url.search);
        } catch (error) {
          captureAppError(error as Error, {
            errorName: 'Deep-link URL validation failed',
            context: {
              'Deep-link Context': {
                storedDeeplink,
                originalError: (error as Error).message,
              },
            },
          });
          // Invalid URL format, fall back to home page
          navigate({ pathname: APP_MANAGED_ROUTES.HOME });
        } finally {
          // Always clean up the stored deep-link to prevent reuse
          sessionStorage.removeItem(STORED_DEEPLINK_SESSION_STORAGE_KEY);
        }
      }

      setHasHandledPostAuth(true);
    }
  }, [isAuthenticated, navigate, location, hasHandledPostAuth]);

  // Return whether this hook has handled post-auth navigation
  // This allows other components to know if they should skip their own navigation logic
  return { hasHandledPostAuth };
};
